import { Translator } from "../contexts/translator";
import { auditFieldTranslations, auditTypeTranslations } from "../constants/audit-constants";

/**
 * 
 * @param type - MasterGroup; Tag; Person / Asset; MasterAsset
 * @param actionSysname - INSERT / UPDATE / DELETE
 * @returns boolean if this represents an assign/unassign relationship instead of create/delete
 */
const isAssignment = (type: string): boolean => {
    if (type === 'MasterGroup') {
        return true;
    }
    else if (type === 'MasterGroupProfile') {
        return true;
    }
    else if (type === 'MasterAsset') {
        return true;
    }
    else if (type === 'MasterFloor') {
        return true;
    }
    else if (type === 'MasterExpiry') {
        return true;
    }

    return false;
}

const getTranslation = (text: string, key: string, translate?: Translator): string => {
    return (translate ? translate.byKey(key) : text);
}

/**
 * 
 * @param type - MasterGroup; Tag; Person / Asset; MasterAsset
 * @param actionSysname - INSERT / UPDATE / DELETE
 * @returns user friendly string value which we can show to the user.
 */
export const getActionName = (type: string, actionSysname: string, translate?: Translator): string => {

    if (actionSysname === 'INSERT') {
        return (isAssignment(type))
            ? getTranslation('Assigned', 'assigned', translate)
            : getTranslation('Created', 'created', translate);
    }
    else if (actionSysname === 'UPDATE') {
        return (isAssignment(type))
            ? getTranslation('Unassigned', 'unassigned', translate)
            : getTranslation('Updated', 'updated', translate);
    }
    else if (actionSysname === 'DELETE') {
        return (isAssignment(type))
            ? getTranslation('Unassigned', 'unassigned', translate)
            : getTranslation('Deleted', 'deleted', translate);
    }

    return actionSysname;
}

/**
 * 
 * @param type - MasterGroup; Tag; Person / Asset; MasterAsset
 * @param actionSysname - INSERT / UPDATE / DELETE
 * @returns user friendly string value which we can show to the user.
 */
export const getEnrolmentType = (type: string, actionSysname: string, translate?: Translator): string => {
    let label: string = '';

    // Check if we can find a user-friendly alternative
    auditTypeTranslations.map((field) => {
        if (field.value === type) {
            label = getTranslation(type, field.label, translate);
            return label;
        }
    })
    
    // Found an alternative. Use it.
    if (!!label && label.length > 0) {
        return label;
    }

    return type;
}

const getGenderLabel = (gender: string, translate?: Translator): string => {
    let label: string = '';
    if (gender === 'F') {
        label = getTranslation('Female', 'female', translate);
    }
    else if (gender === 'M') {
        label = getTranslation('Male', 'male', translate);
    }
    else {
        label = getTranslation('Unspecified', 'unspecified', translate);
    }

    return label
}

/**
 * @param fieldValue - The value. May or may not be user-friendly.
 * @param fieldSysname - name of the field that was edited.
 * @returns user friendly string value which we can show to the user.
 */
export const getEnrolmentValue = (fieldValue: string, fieldSysname: string, translate?: Translator): string => {

    if (fieldSysname === 'mstGender') { // Gender
        return getGenderLabel(fieldValue, translate);
    }
    else if (fieldSysname === 'mstCurrent' || fieldSysname === 'mstNoapb'
        || fieldSysname === 'tagAccessOverride' || fieldSysname === 'tagBlacklist'
        || fieldSysname === 'tagGuardTour' || fieldSysname === 'tagSuspend') {
        return (fieldValue === '0')
            ? getTranslation('Disabled', 'disabled', translate)
            : getTranslation('Enabled', 'enabled', translate);
    }

    return fieldValue; // Most values can just be shown as is.
}

/**
 * 
 * @param fieldSysname - name of the field that was edited. Not user friendly.
 * @returns user friendly string value which we can show to the user.
 */
export const getEnrolmentField = (fieldSysname: string, translate?: Translator): string => {
    let label: string = '';

    // These are FK relationships. Show nothing for the field name.
    if (fieldSysname === 'accessGroup') {
        return ''; // No need to show the field name
    }
    else if (fieldSysname === 'masterGroupProfile') {
        return ''; // No need to show the field name
    }
    else if (fieldSysname === 'masterDetailType') {
        return ''; // No need to show the field name
    }
    else if (fieldSysname === 'asset' || fieldSysname === 'master') {
        return ''; // No need to show the field name
    }
    else if (fieldSysname === 'bioTemplateFormat') {
        return ''; // No need to show the field name
    }

    // Check if we can find a user-friendly alternative
    auditFieldTranslations.map((field) => {
        if (field.value === fieldSysname) {
            label = getTranslation(fieldSysname, field.label, translate);
            return label;
        }
    })

    // Found an alternative. Use it.
    if (!!label && label.length > 0) {
        return label;
    }

    return fieldSysname;
}